/* eslint-disable import/order */
import React, { useEffect } from 'react';

import { createRoot, Root } from 'react-dom/client';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import * as mobx from 'mobx';
import { Provider } from 'mobx-react';
import i18next from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { createRootEnv } from '@bringg-frontend/bringg-web-infra';
import { AppConfig } from '@bringg-frontend/bringg-web-infra';
import { sentryNotifier } from '@bringg-frontend/bringg-web-infra';
import { registerAction, executeAction } from '@bringg-frontend/global-stores';

// We don't import the entire lib only the needed parts for initialization so the React.lazy on the Routes will actually split the bundle
import { App, storesInitializer } from '@bringg-frontend/bringg-web-legacy/for-app';
import { phoneNumberService } from '@bringg-frontend/utils';

// It may seem that most of the app is in the Routes file but actually, all the stores and some other things are imported in this file and the App.tsx
// Which reduce the bundle and the source map significantly when we use the code splitting
const Routes = React.lazy(async () => import('./Routes'));

const SdkActions = Object.freeze({
	UPDATE_SDK: 'sdk updated'
});

function NotifyRendered({ onRendered }: { onRendered: () => void }) {
	useEffect(() => {
		onRendered();
	}, [onRendered]);
	return null;
}

let appRoot: Root | null = null;

async function loadReactOnLoad() {
	// Meaning onload event already fired, this happens on first load of the React app
	if (document.readyState === 'complete') {
		await loadReact();
		return;
	}

	window.addEventListener('load', loadReact);
}

const SDK_HANDSHAKE_TIMEOUT_MS = 60_000;
const SDK_HANDSHAKE_POLL_MS = 20;

/**
 * The Angular app in scripts/core/bringg-sdk.js and this app hand the SDK instance to each other
 * through window markers, and either side can reach the rendezvous first. We publish the _bringg
 * marker; when Angular's login flow runs afterwards it notices that marker and initialises the SDK
 * itself, resolving the sdkInstance promise. When Angular's login flow ran first instead, it left
 * an initSdk callback behind for us to invoke.
 *
 * Checking the initSdk marker only once loses the race whenever React boots ahead of that login
 * flow: nobody triggers the initialisation, the SDK init call then waits on an sdkInstance deferred
 * that Angular never resolves, and the app never renders, so every Angular-triggered React modal is
 * missing. Waiting for whichever side arrives makes the rendezvous order-independent.
 */
async function handshakeWithAngularSdk(): Promise<void> {
	let angularInitialisedItself = false;
	let watchingSdkInstance = false;
	const deadline = Date.now() + SDK_HANDSHAKE_TIMEOUT_MS;

	while (Date.now() < deadline) {
		if (window.initSdk) {
			// eslint-disable-next-line @typescript-eslint/await-thenable
			await window.initSdk(window._bringg);
			return;
		}

		if (angularInitialisedItself) {
			return;
		}

		if (window.sdkInstance && !watchingSdkInstance) {
			watchingSdkInstance = true;
			void Promise.resolve(window.sdkInstance)
				.then(() => {
					angularInitialisedItself = true;
				})
				.catch(() => {});
		}

		await new Promise(resolve => setTimeout(resolve, SDK_HANDSHAKE_POLL_MS));
	}

	console.error('SDK handshake with the Angular app timed out - initialising React on its own SDK');
}

async function loadReact() {
	try {
		window._bringg = { mobx, phoneNumberService };

		await handshakeWithAngularSdk();

		await initReactApp();
	} catch (error) {
		console.error('Failed to initReactApp', error);
	}
}

loadReactOnLoad();

async function initReactApp() {
	const rootEnv = await createRootEnv();
	const { dashboardSdk } = rootEnv;

	try {
		await dashboardSdk.init();
	} catch (error) {
		console.error('Failed to connect SDK in react app', error);
	}

	// on bringg web logout re init react
	registerAction(
		SdkActions.UPDATE_SDK,
		() => {
			appRoot?.unmount();
			appRoot = null;
			initReactApp();
		},
		dashboardSdk.sdk.crossAppTransport
	);

	let resolve;
	const nakedPromise = new Promise(prResolve => {
		resolve = prResolve;
	});

	await i18next
		.use(initReactI18next)
		.use(HttpBackend)
		.init(AppConfig.getTranslationConfig(), () => {
			resolve();
		});

	const rootStore = storesInitializer.init(rootEnv);
	const { views, data } = rootStore;
	const { authStore } = views;

	await views.authStore.afterLoginEvents();
	views.authStore.setLogin(true);
	views.initLogRocket();
	sentryNotifier.setUserOnScope();

	await nakedPromise;
	// on this point i18next is initialized and we start language setup - on the day we remove login from angular we can remove all this flow
	views.syncLanguageWithAngular();

	appRoot = createRoot(document.getElementById('root')!);
	appRoot.render(
		<I18nextProvider i18n={i18next}>
			<Provider {...views} {...data}>
				<App Routes={Routes} />
				<NotifyRendered onRendered={() => executeAction(authStore.crossAppTransport, 'REACT_DONE_RENDERED')} />
			</Provider>
		</I18nextProvider>
	);
}
